import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  catchError,
  combineLatest,
  finalize,
  map,
  of,
  switchMap,
  take,
  Subscription,
} from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import {
  TuiDialogContext,
  TuiDataList,
  TuiLoader,
  TuiDropdown,
  TuiButton,
  TuiTextfield,
  TuiLabel,
} from '@taiga-ui/core';
import {
  TuiPreview,
  TuiPreviewDialogService,
  TuiProgress,
  TuiComboBox,
  TuiMultiSelect,
  TuiChevron,
  TuiInputChip,
} from '@taiga-ui/kit';
import type { Counterparty, RequestFileCategory } from '@core/types';
import type { RequestModel } from '@core/models';
import { OutkeepPermission } from '@outkeep/shared';
import { AccountService } from '@core/services/account/account.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { RequestService } from '@core/services/request/request.service';
import { XAlertService } from '@core/services/x-alert.service';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { GroupFilesUploadManagerService } from './group-files-upload-manager.service';
import type {
  GroupUploadFile,
  GroupUploadResult,
} from './group-files-upload.types';

const MAX_FILES = 150;
const MAX_FILE_SIZE = 50_000_000;

@Component({
  selector: 'x-group-files-upload-group-actions-view',
  imports: [
    TuiTextfield,
    TuiLabel,
    TuiChevron,
    TuiInputChip,
    CommonModule,
    ReactiveFormsModule,
    TuiButton,
    TuiComboBox,
    TuiDataList,
    TuiDropdown,
    TuiLoader,
    TuiMultiSelect,
    TuiPreview,
    TuiProgress,
    TranslatePipe,
  ],
  providers: [GroupFilesUploadManagerService],
  templateUrl: './group-files-upload-group-actions-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupFilesUploadGroupActionsViewComponent
  implements OnInit, OnDestroy
{
  private readonly requestService = inject(RequestService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly accountService = inject(AccountService);
  private readonly permissionService = inject(OutkeepPermissionService);
  private readonly alertService = inject(XAlertService);
  private readonly translateService = inject(OutkeepTranslateService);
  private readonly previewDialogService = inject(TuiPreviewDialogService);
  private readonly destroyRef = inject(DestroyRef);

  readonly uploadManager = inject(GroupFilesUploadManagerService);
  readonly categoryControl = new FormControl<RequestFileCategory | null>(
    null,
    Validators.required,
  );
  readonly $requests = signal<RequestModel[]>([]);
  readonly $eligibleRequests = signal<RequestModel[]>([]);
  readonly $categories = signal<RequestFileCategory[]>([]);
  readonly $files = signal<GroupUploadFile[]>([]);
  readonly $results = signal<GroupUploadResult[]>([]);
  readonly $loading = signal(true);
  readonly $uploading = signal(false);
  readonly $dragging = signal(false);
  readonly $missingRequestsCount = signal(0);
  readonly $categorySearch = signal('');
  readonly $requestSearches = signal(new Map<GroupUploadFile['id'], string>());
  readonly $previewUrl = signal<string | null>(null);
  readonly $ownerCategories = computed(() =>
    this.filterCategories(
      this.$categories().filter(category => !category.isContractorFileCat),
    ),
  );
  readonly $contractorCategories = computed(() =>
    this.filterCategories(
      this.$categories().filter(category => category.isContractorFileCat),
    ),
  );

  readonly categoryStringify = (category: RequestFileCategory): string =>
    category.name;
  readonly requestStringify = (request: RequestModel): string =>
    request.carVin
      ? `${request.requestNumber} (${request.carVin})`
      : request.requestNumber;

  private readonly targetControls = new Map<
    GroupUploadFile['id'],
    FormControl<RequestModel[]>
  >();
  private readonly targetControlSubscriptions = new Map<
    GroupUploadFile['id'],
    Subscription
  >();
  private fileSequence = 0;

  private readonly canUploadAll = this.permissionService.$hasPermission(
    OutkeepPermission.LogistFilesUpload,
  );
  private readonly canUploadOwner = this.permissionService.$hasPermission(
    OutkeepPermission.LogistOnlyOwnerFilesUpload,
  );
  private readonly canUploadContractor = this.permissionService.$hasPermission(
    OutkeepPermission.LogistOnlyContractorFilesUpload,
  );
  private readonly canEditAnyContractor = this.permissionService.$hasPermission(
    OutkeepPermission.LogistEditRequestsOfAnyContractor,
  );

  @ViewChild('imagePreview')
  readonly imagePreview?: TemplateRef<TuiDialogContext>;

  ngOnInit(): void {
    combineLatest([
      this.requestService.loadSelectedRequests$(),
      this.accountService
        .getCurrentUserCounterparties$()
        .pipe(catchError(() => of([] as Counterparty[]))),
    ])
      .pipe(
        switchMap(([requests, counterparties]) => {
          this.$requests.set(requests);
          this.$missingRequestsCount.set(
            Math.max(
              0,
              this.requestService.selectedRequestsCount() - requests.length,
            ),
          );

          const counterpartyIds = [
            ...new Set(
              requests.flatMap(request => [
                request.counterpartyOwnerId?.id,
                request.contractorCounterpartyId?.id,
              ]),
            ),
          ].filter((id): id is number => typeof id === 'number');

          return this.requestFilesService
            .getRequestFileCategories$(counterpartyIds)
            .pipe(
              map(categories => ({
                categories: categories.filter(category =>
                  this.canUseCategory(category),
                ),
                counterparties,
              })),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.$loading.set(false)),
      )
      .subscribe({
        next: ({ categories, counterparties }) => {
          this.$categories.set(categories);
          this.currentUserCounterparties = counterparties;
        },
        error: () => {
          this.alertService.error(this.translate('errors.load'));
        },
      });

    this.categoryControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(category => this.resetAssignmentsForCategory(category));
  }

  private currentUserCounterparties: Counterparty[] = [];

  targetControl(fileId: GroupUploadFile['id']): FormControl<RequestModel[]> {
    const existing = this.targetControls.get(fileId);

    if (existing) {
      return existing;
    }

    const control = new FormControl<RequestModel[]>([], { nonNullable: true });

    const subscription = control.valueChanges.subscribe(requests => {
      this.$files.update(files =>
        files.map(file =>
          file.id === fileId
            ? {
                ...file,
                targetRequestIds: requests.map(request => request.id),
                distribution: 'default',
                ambiguousRequestIds: [],
              }
            : file,
        ),
      );
    });

    this.targetControls.set(fileId, control);
    this.targetControlSubscriptions.set(fileId, subscription);

    return control;
  }

  async addFiles(files: Iterable<File>): Promise<void> {
    const accepted: File[] = [];
    const remainingCapacity = MAX_FILES - this.$files().length;

    for (const file of files) {
      if (accepted.length >= remainingCapacity) {
        this.alertService.error(
          this.translate('errors.max_files', { maxFiles: MAX_FILES }),
        );
        break;
      }
      if (!file.size) {
        this.alertService.error(
          this.translate('errors.empty_file', { name: file.name }),
        );
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        this.alertService.error(
          this.translate('errors.file_too_large', { name: file.name }),
        );
        continue;
      }
      accepted.push(file);
    }

    const targetRequestIds = this.$eligibleRequests().map(
      request => request.id,
    );
    const stagedFiles = accepted.map(file => {
      const stagedFile: GroupUploadFile = {
        id: `${Date.now()}-${this.fileSequence++}`,
        file,
        targetRequestIds,
        distribution: 'default',
        ambiguousRequestIds: [],
        previewUrl: this.isImage(file) ? URL.createObjectURL(file) : undefined,
      };
      const control = this.targetControl(stagedFile.id);

      control.setValue(this.$eligibleRequests(), { emitEvent: false });

      return stagedFile;
    });

    this.$files.update(current => [...current, ...stagedFiles]);
  }

  onFileInput(input: HTMLInputElement): void {
    if (input.files) {
      void this.addFiles(Array.from(input.files));
    }
    input.value = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.$dragging.set(false);

    if (!this.$uploading() && event.dataTransfer?.files) {
      void this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  removeFile(fileId: GroupUploadFile['id']): void {
    if (this.$uploading()) {
      return;
    }

    const file = this.$files().find(item => item.id === fileId);

    if (file?.previewUrl) {
      URL.revokeObjectURL(file.previewUrl);
    }
    this.targetControlSubscriptions.get(fileId)?.unsubscribe();
    this.targetControlSubscriptions.delete(fileId);
    this.targetControls.delete(fileId);
    this.$requestSearches.update(searches => {
      const updatedSearches = new Map(searches);

      updatedSearches.delete(fileId);

      return updatedSearches;
    });
    this.$files.update(files => files.filter(item => item.id !== fileId));
    this.$results.update(results =>
      results.filter(result => result.fileId !== fileId),
    );
  }

  selectAllRequests(fileId: GroupUploadFile['id']): void {
    if (!this.$uploading()) {
      this.targetControl(fileId).setValue(this.$eligibleRequests());
    }
  }

  clearAllRequests(fileId: GroupUploadFile['id']): void {
    if (!this.$uploading()) {
      this.targetControl(fileId).setValue([]);
    }
  }

  requestSearch(fileId: GroupUploadFile['id']): string {
    return this.$requestSearches().get(fileId) ?? '';
  }

  setRequestSearch(fileId: GroupUploadFile['id'], search: string | null): void {
    this.$requestSearches.update(searches => {
      const updatedSearches = new Map(searches);

      updatedSearches.set(fileId, search ?? '');

      return updatedSearches;
    });
  }

  filteredRequests(fileId: GroupUploadFile['id']): RequestModel[] {
    const search = this.requestSearch(fileId).trim().toLowerCase();

    if (!search) {
      return this.$eligibleRequests();
    }

    return this.$eligibleRequests().filter(request =>
      [request.requestNumber, request.carVin]
        .filter((value): value is string => Boolean(value))
        .some(value => value.toLowerCase().includes(search)),
    );
  }

  distributeFiles(): void {
    if (this.$uploading()) {
      return;
    }

    const distributed = this.uploadManager.distributeFiles(
      this.$files().map(file => ({
        ...file,
        distribution: 'default',
        ambiguousRequestIds: [],
      })),
      this.$eligibleRequests(),
    );

    distributed.forEach(file => {
      const targets = this.$eligibleRequests().filter(request =>
        file.targetRequestIds.includes(request.id),
      );

      this.targetControl(file.id).setValue(targets, { emitEvent: false });
    });
    this.$files.set(distributed);
    this.$results.set([]);
  }

  canUpload(): boolean {
    return Boolean(
      this.categoryControl.valid &&
      this.$files().length &&
      !this.$results().length &&
      !this.$uploading() &&
      !this.$missingRequestsCount() &&
      this.$files().every(
        file =>
          file.distribution !== 'ambiguous' && file.targetRequestIds.length > 0,
      ),
    );
  }

  upload(): void {
    const category = this.categoryControl.value;

    if (!category || !this.canUpload()) {
      return;
    }

    this.setUploadingState(true);
    this.uploadManager
      .upload$(category, this.$files(), this.$requests())
      .pipe(
        take(1),
        finalize(() => this.setUploadingState(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(results => {
        this.$results.set(results);
        const failures = results.filter(result => result.status !== 'success');

        if (failures.length) {
          this.alertService.error(
            this.translate('errors.upload', {
              failed: failures.length,
              total: results.length,
            }),
          );
        } else {
          this.alertService.success(this.translate('success'));
        }
      });
  }

  retryFailed(): void {
    const category = this.categoryControl.value;
    const failed = this.$results().filter(
      result => result.status !== 'success',
    );

    if (!category || !failed.length || this.$uploading()) {
      return;
    }

    this.setUploadingState(true);
    this.uploadManager
      .retryFailed$(category, this.$files(), this.$requests(), failed)
      .pipe(
        take(1),
        finalize(() => this.setUploadingState(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(retried => {
        const retriedByPair = new Map(
          retried.map(result => [
            `${result.fileId}:${result.requestId}`,
            result,
          ]),
        );

        this.$results.update(results =>
          results.map(
            result =>
              retriedByPair.get(`${result.fileId}:${result.requestId}`) ??
              result,
          ),
        );
      });
  }

  showPreview(url: string): void {
    if (!this.imagePreview) {
      return;
    }

    this.$previewUrl.set(url);
    this.previewDialogService
      .open(this.imagePreview)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  resultFor(fileId: string, requestId: number): GroupUploadResult | undefined {
    return this.$results().find(
      result => result.fileId === fileId && result.requestId === requestId,
    );
  }

  hasFailures(): boolean {
    return this.$results().some(result => result.status !== 'success');
  }

  formatFileSize(size: number): string {
    return size < 1_000_000
      ? this.translate('file_size_kb', { size: Math.ceil(size / 1_000) })
      : this.translate('file_size_mb', {
          size: (size / 1_000_000).toFixed(1),
        });
  }

  private translate(
    key: string,
    params?: Record<string, number | string>,
  ): string {
    return this.translateService.instant(
      `group_actions.files_upload.${key}`,
      params,
    );
  }

  ngOnDestroy(): void {
    this.targetControlSubscriptions.forEach(subscription =>
      subscription.unsubscribe(),
    );
    this.targetControlSubscriptions.clear();
    this.$files().forEach(file => {
      if (file.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
    });
  }

  private resetAssignmentsForCategory(
    category: RequestFileCategory | null,
  ): void {
    const eligibleRequests = category
      ? this.uploadManager
          .getEligibleRequests(category, this.$requests())
          .filter(request => this.canUseRequest(category, request))
      : [];

    this.$eligibleRequests.set(eligibleRequests);
    this.$files.update(files =>
      files.map(file => ({
        ...file,
        targetRequestIds: eligibleRequests.map(request => request.id),
        distribution: 'default',
        ambiguousRequestIds: [],
      })),
    );
    this.targetControls.forEach(control =>
      control.setValue(eligibleRequests, { emitEvent: false }),
    );
    this.$results.set([]);
  }

  private canUseCategory(category: RequestFileCategory): boolean {
    return category.isContractorFileCat
      ? this.canUploadAll() || this.canUploadContractor()
      : this.canUploadAll() || this.canUploadOwner();
  }

  private canUseRequest(
    category: RequestFileCategory,
    request: RequestModel,
  ): boolean {
    if (this.canEditAnyContractor()) {
      return true;
    }

    const targetCounterpartyId = category.isContractorFileCat
      ? request.contractorCounterpartyId?.id
      : request.counterpartyOwnerId?.id;

    return this.currentUserCounterparties.some(
      counterparty => counterparty.id === targetCounterpartyId,
    );
  }

  private filterCategories(
    categories: RequestFileCategory[],
  ): RequestFileCategory[] {
    const search = this.$categorySearch().trim().toLowerCase();

    return search
      ? categories.filter(category =>
          category.name.toLowerCase().includes(search),
        )
      : categories;
  }

  private isImage(file: File): boolean {
    return (
      file.type.startsWith('image/') ||
      /\.(avif|bmp|gif|jpe?g|png|webp)$/i.test(file.name)
    );
  }

  private setUploadingState(uploading: boolean): void {
    this.$uploading.set(uploading);

    if (uploading) {
      this.categoryControl.disable({ emitEvent: false });
      this.targetControls.forEach(control =>
        control.disable({ emitEvent: false }),
      );
    } else {
      this.categoryControl.enable({ emitEvent: false });
      this.targetControls.forEach(control =>
        control.enable({ emitEvent: false }),
      );
    }
  }
}
